import { useMemo } from "react";

import { useStaffRoles } from "../access/use-staff-roles";
import { useConsoleFeatures } from "../home/queries";
import { type AreaKey, isAreaOpenable, type Offering } from "./offering";

/**
 * The offering of the signed-in staff member: their roles and the features the API switched on. Every screen that decides whether to show
 * an area or a link to it asks this, so no page reads a feature switch by itself. While the roles or the features load, `rolesLoading` or
 * `featuresLoading` is true; the roles are empty and every feature is off until they arrive, so nothing is offered early.
 */
export function useOffering(): Offering &
  Readonly<{ rolesLoading: boolean; featuresLoading: boolean }> {
  const { roles, isLoading: rolesLoading } = useStaffRoles();
  const { features, isLoading: featuresLoading } = useConsoleFeatures();
  return useMemo(
    () => ({ roles, features, rolesLoading, featuresLoading }),
    [roles, features, rolesLoading, featuresLoading],
  );
}

/**
 * The address for a link to another area, or `null` when the area does not open for this staff member (or its feature is off). The caller
 * shows plain text, or nothing, instead of a link that would end in a refusal. A link grants nothing: the page and the API check again.
 */
export function useAreaHref(): (area: AreaKey, to: string) => string | null {
  const offering = useOffering();
  return useMemo(() => (area, to) => (isAreaOpenable(area, offering) ? to : null), [offering]);
}
